'use client';

import { useEffect } from 'react';
import { GlassBar } from './glass-bar';
import { hasNativePlugin, isIOS, isNative } from './is-native';
import { sheetStackDepth, subscribeSheetStack } from '@/components/ui/sheet-stack';

/**
 * Whether the iPhone's own swipe in from the left edge should go Back right
 * now (Tom, 2026-10-12: "a from left to right swipe to go back to the list
 * where you left off").
 *
 * Off while a sheet is open. A sheet is not a page in the history, so the
 * swipe would leave the page UNDER it, and a half-filled form would go with
 * the page — Back has to close the sheet first, and on iOS the sheet has its
 * own close. Off while something covers the app (the lock screen, a full
 * screen map), which brings its own way out.
 */
export function backSwipeAllowed(openSheets: number, covered: boolean): boolean {
  return openSheets === 0 && !covered;
}

/** Keeps the native edge swipe in step with the sheets and covers. iOS only. */
export function useNativeBackSwipe(covered: boolean): void {
  useEffect(() => {
    if (!isNative() || !hasNativePlugin('MuckaGlassBar') || !isIOS()) return;
    let last: boolean | null = null;
    const apply = () => {
      const enabled = backSwipeAllowed(sheetStackDepth(), covered);
      if (enabled === last) return;
      last = enabled;
      // An app build from before this method answers "not implemented".
      GlassBar.setBackSwipe({ enabled }).catch(() => {});
    };
    apply();
    return subscribeSheetStack(apply);
  }, [covered]);
}

/**
 * What Android's Back does: close the open sheet if there is one — through the
 * sheet's own Escape, so an edited form still asks "Save before closing?" —
 * and only otherwise go back a page.
 */
export function backButtonAction(openSheets: number): 'close-sheet' | 'go-back' {
  return openSheets > 0 ? 'close-sheet' : 'go-back';
}
